
import { Validatable } from "../mixins/validatable";
import {toFixedWithPrecision} from "../utils";

export default Validatable.extend({
	inheritAttrs: true,
	data() {
		return {
			focused: false,
			resetTriggered: false,
			isNegative: false
		};
	},
	props: {
		value: {
			type: Number,
			default: 0
		},
		decimals: {
			type: Number,
			default: 2
		},
		calculatedDecimals: {
			type: Number,
			default: 8
		},
		min: {
			type: [Number, String]
		},
		max: {
			type: [Number, String]
		},
		tabindex: [Number, String],
		preventTab: Boolean,
		hasError: Boolean,
		icon: String,
		iconLeft: String,
		forceNumber: Boolean,
		step: Number,
		autofocus: Boolean
	},
	watch: {
		value: {
			immediate: true,
			handler(){
				if (this.value == 0) return;
				this.isNegative = this.value < 0;
			}
		}
	},
	computed: {
		minValue():number | undefined {
			if (this.min == undefined) {
				return undefined;
			}
			if (typeof(this.min)=="string") {
				return parseInt(this.min);
			}
			return this.min;
		},
		maxValue():number | undefined {
			if (this.max == undefined) {
				return undefined;
			}
			if (typeof(this.max)=="string") {
				return parseInt(this.max);
			}
			return this.max;
		},
		negative():Boolean {
			if (this.minValue == undefined) {
				return true;
			}
			if (this.minValue >= 0) {
				return false;
			}
			return true;
		},
		listeners():any {
			let listeners = {...this.$listeners};
			delete listeners.input;
			return listeners;
		},
		text():string | number {
			if (this.focused && isNaN(this.value)){
				if (this.isNegative){
					return "-";
				}
				return "";
			}
			if (this.value == 0 && this.isNegative){
				return "-";
			}
			if (this.resetTriggered) {
				return `${this.value}`;
			}
			return toFixedWithPrecision(this.value || 0,this.decimals);
		},
		pattern():RegExp {
			if (this.calculatedDecimals == 0) {
				return /^\-?[0-9]*$/;
			}
			return new RegExp(`^\\-?[0-9]*([\\.\\,]([0-9]{0,${this.calculatedDecimals}})*)?$`);
		},
		iconClickHandler():Function | undefined {
			return this.$listeners["icon-click"] as Function || undefined;
		},
		iconLeftClickHandler():Function | undefined {
			return this.$listeners["icon-click"] as Function || undefined;
		}
	},
	methods: {
		onFocus() {
			this.focused = true;
		},
		blur(){
			if (!this.$refs.inputElement) return;
			(this.$refs.inputElement as any).blur();
		},
		onBlur() {
			this.resetTriggered = false;
			this.focused = false;
			if (this.maxValue != undefined) {
				if (this.value > this.maxValue) {
					this.$emit("input", this.maxValue);
				}
			}
			if (this.minValue != undefined) {
				if (this.value < this.minValue) {
					this.$emit("input", this.minValue);
				}
			}
		},
		focus() {
			(this.$refs.inputElement as HTMLInputElement).focus();
		},
		onInput(event:string) {
			event = event.replace(",", ".");
			let val = event || "0";
			if (this.isNegative && val.charAt(0) != "-"){
				val = `-${val}`;
			}
			if(val == "-"){
				this.$emit("input", "-");
			}
			this.$emit("input", parseFloat(val));
		},
		onKeyUp(event:KeyboardEvent) {
			let e = event.key;
			if (event.ctrlKey) {
				return;
			}
			if (event.key == "Alt") {
				return;
			}
			if (e.charAt(0) == "F") {
				return;
			}

			if (event.shiftKey && event.which == 9) {
				return;
			}
		},
		// eslint-disable-next-line max-lines-per-function
		onKeyPress(event:KeyboardEvent) {
			if (event.ctrlKey) {
				return;
			}

			if (event.shiftKey && event.which == 9) {
				return;
			}
			if (event.key == "Alt") {
				return;
			}
			// Focus next on  tab and enter
			if ([9, 13].indexOf(event.which) != -1) {
				return;
			}

			if ([20, 16].indexOf(event.which) != -1) {
				return;
			}

			if (event.key.startsWith("Arrow")) {
				return;
			}

			let element = event.target as HTMLInputElement;
			let val = element.value;
			let decimals = val.split(".")[1];
			decimals = decimals || "";

			if (!this.resetTriggered) {
				this.isNegative = false;
				if (event.key == "-"){
					if (this.minValue !== undefined && this.minValue >= 0) {
						this.$emit("input", this.minValue);
					}else{
						this.isNegative = true;
						this.$emit("input", -0.0);
					}
				} else if (event.key.match(/^[0-9]$/)) {
					if (decimals.length >= this.decimals) {
						event.preventDefault();
					}
					this.$emit("input", parseFloat(event.key));
				} else if (event.key == "." || event.key == ",") {
					if (this.minValue !== undefined && this.minValue >= 0) {
						this.$emit("input", this.minValue);
					}else{
						this.$emit("input",  0.0);
					}
				} else if (this.minValue !== undefined && this.minValue >= 0){
					this.$emit("input", this.minValue);
				}else{
					this.$emit("input", 0);
				}
				this.resetTriggered = true;
				return event.preventDefault();
			}



			if (event.key == "-"){
				this.isNegative = !this.isNegative;
				if (this.isNegative && element.value.charAt(0) != "-"){
					element.value = `-${element.value || 0}`;
				}else if (!this.isNegative && element.value.charAt(0) == "-") {
					element.value = element.value.slice(1, element.value.length);
				}
				this.$emit("input", parseFloat(element.value));
				event.preventDefault();
			}

			// disallow whitespaces
			if ([32, 12].indexOf(event.which) != -1) {
				event.preventDefault();
			}

			// allow navkeys
			if (event.keyCode > 7 && event.which  < 47) {
				return;
			}

			// Disallow letters
			if (event.key.match(/^[a-zA-Z]$/)) {
				event.preventDefault();
			}

			// Allow only one decimal seperator;
			let decimalPointIndex = val.indexOf(".");
			if (decimalPointIndex == -1) {
				decimalPointIndex = val.indexOf(",");
			}
			if (decimalPointIndex == -1) {
				if (event.key.match(/[\,\.]/)) {
					return;
				}
			}

			if (event.key.match(/^[0-9]$/)) {
				if (val.indexOf(".") != -1){
					let isSelectionBeforeDecimal = (element.selectionStart || 0) <= val.indexOf(".");
					if (isSelectionBeforeDecimal) return;
					if (decimals.length < this.decimals) return;
					let selectionIsMultipleCharacters = (element.selectionStart || 0) < (element.selectionEnd || 0);
					if (selectionIsMultipleCharacters) return;
					event.preventDefault();
				}
				return;
			}

			event.preventDefault();
		}
	},
});
